import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { listFundRequests } from '../../../services/fundRequestService';
import { t } from '../../../utils/textUtils';
import { extractRomanNumeral, formatCurrency, computeTotalProgressPercent } from '../utils/formatters';
import { useDashboardActions } from '../hooks/useDashboardActions';
import {
  fetchProjects,
  fetchModularUnits,
  fetchWorkCategories,
  fetchTasks,
  fetchProjectDashboardData,
  fetchUnitDashboardData,
  fetchDashboardByProject,
  fetchDashboardByUnit,
  fetchDashboardByCategory
} from '../utils/dashboardApi';
import ProjectSelector from './ProjectSelector';
import UnitSelector from './UnitSelector';
import CategorySelector from './CategorySelector';
import FundSummarySection from './FundSummarySection';
import ProgressOverviewSection from './ProgressOverviewSection';
import ChartSection from './ChartSection';
import ProgressBangunanTable from './ProgressBangunanTable';
import TaskPerKategoriTable from './TaskPerKategoriTable';

const ProjectOverviewComponent = () => {
  const createEmptyFundSummary = () => ({
    totalAmount: 0,
    totalRequests: 0,
    releasedCount: 0,
    approvedCount: 0,
    averagePerRequest: 0,
    lastReleaseDate: null,
    firstReleaseDate: null
  });

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedBangunan, setSelectedBangunan] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [projectList, setProjectList] = useState([]);
  const [bangunanList, setBangunanList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [overviewData, setOverviewData] = useState({
    percentComplete: 0,
    milestoneDue: [],
    lateTasks: [],
    categoryProgress: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // New states for unit bangunan mode
  const [isUnitMode, setIsUnitMode] = useState(false);
  const [categoryTasks, setCategoryTasks] = useState([]);
  const [unitOverviewData, setUnitOverviewData] = useState({
    completePercent: 0,
    progressPerKategori: [],
    tasksPerKategori: [],
    milestoneDue: [],
    lateTasks: []
  });

  // New states for project mode
  const [isProjectMode, setIsProjectMode] = useState(false);
  const [projectOverviewData, setProjectOverviewData] = useState({
    completePercent: 0,
    progressPerBangunan: [],
    progressKategori: [],
    tasksPerKategori: [],
    milestoneDue: [],
    lateTasks: [],
    bangunanMilestones: []
  });

  // State untuk API baru
  const [dataByProject, setDataByProject] = useState(null);
  const [dataByUnit, setDataByUnit] = useState([]);
  const [dataByCategory, setDataByCategory] = useState([]);
  const [fundSummary, setFundSummary] = useState(createEmptyFundSummary());
  const [fundSummaryLoading, setFundSummaryLoading] = useState(false);
  const [fundSummaryError, setFundSummaryError] = useState(null);

  
  // Load project data and dashboard overview on component mount
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const projects = await fetchProjects();
        setProjectList(Array.isArray(projects) ? projects : []);

      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setProjectList([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Function to sort categories by Roman numeral
  const sortCategoriesByRoman = useCallback((categories) => {
    return [...categories].sort((a, b) => {
      const aOrder = extractRomanNumeral(a.kategori || a.name);
      const bOrder = extractRomanNumeral(b.kategori || b.name);
      return aOrder - bOrder;
    });
  }, []);

  // Function to calculate overview data from tasks
  const calculateOverviewData = useCallback(async (tasks) => {
    if (!tasks || tasks.length === 0) {
      return {
        percentComplete: 0,
        milestoneDue: [],
        lateTasks: [],
        categoryProgress: []
      };
    }

    const totalTasks = tasks.length;
    const totalProgress = tasks.reduce((sum, task) => sum + (task.progress || 0), 0);
    const percentComplete = totalTasks > 0 ? Math.round(totalProgress / totalTasks) : 0;

    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    const milestoneDue = tasks
      .filter(task => {
        const endDate = new Date(task.endDate);
        const now = new Date();
        return endDate >= now && endDate <= thirtyDaysFromNow;
      })
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
      .slice(0, 10)
      .map(task => ({
        id: task.id,
        name: task.name,
        finish: task.endDate
      }));

    const now = new Date();
    const lateTasks = tasks
      .filter(task => {
        const endDate = new Date(task.endDate);
        return endDate < now && (task.progress || 0) < 100;
      })
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
      .slice(0, 10)
      .map(task => ({
        id: task.id,
        name: task.name,
        start: task.startDate,
        finish: task.endDate,
        duration: `${task.duration || 0} hari`,
        percentComplete: task.progress || 0,
        resourceNames: 'Tim Proyek'
      }));

    const categoryProgress = await Promise.all(
      categoryList.map(async (category) => {
        try {
          const categoryTasks = await fetchTasks(category.id);
          const catTotalTasks = categoryTasks.length;
          const catTotalProgress = categoryTasks.reduce((sum, task) => sum + (task.progress || 0), 0);
          const catProgress = catTotalTasks > 0 ? Math.round(catTotalProgress / catTotalTasks) : 0;
          
          return {
            name: category.name,
            progress: catProgress
          };
        } catch (error) {
          console.error(`Error fetching tasks for category ${category.id}:`, error);
          return {
            name: category.name,
            progress: 0
          };
        }
      })
    );

    return {
      percentComplete,
      milestoneDue,
      lateTasks,
      categoryProgress: sortCategoriesByRoman(categoryProgress)
    };
  }, [categoryList, sortCategoriesByRoman]);

  // Load bangunan data when project changes
  useEffect(() => {
    const loadBangunanUnits = async () => {
      if (selectedProject) {
        try {
          setLoading(true);
          setError(null);
          const bangunanUnits = await fetchModularUnits(selectedProject);
          setBangunanList(Array.isArray(bangunanUnits) ? bangunanUnits : []);
          setSelectedBangunan('');
          setSelectedCategory('');
        } catch (err) {
          setError('Failed to load bangunan units');
          console.error('Error loading bangunan units:', err);
          setBangunanList([]);
        } finally {
          setLoading(false);
        }
      } else {
        setBangunanList([]);
        setSelectedBangunan('');
        setSelectedCategory('');
      }
    };

    loadBangunanUnits();
  }, [selectedProject]);

  // Load categories when bangunan changes
  useEffect(() => {
    const loadWorkCategories = async () => {
      if (selectedBangunan) {
        try {
          setLoading(true);
          setError(null);
          const workCategories = await fetchWorkCategories(selectedBangunan);
          setCategoryList(Array.isArray(workCategories) ? workCategories : []);
          setSelectedCategory('');
        } catch (err) {
          setError('Failed to load work categories');
          console.error('Error loading work categories:', err);
          setCategoryList([]);
        } finally {
          setLoading(false);
        }
      } else {
        setCategoryList([]);
        setSelectedCategory('');
      }
    };

    loadWorkCategories();
  }, [selectedBangunan]);

  // Load overview data when category changes
  useEffect(() => {
    const loadOverviewData = async () => {
      if (selectedCategory) {
        try {
          setLoading(true);
          setError(null);
          const tasks = await fetchTasks(selectedCategory);
          setCategoryTasks(tasks);
          const overviewData = await calculateOverviewData(tasks);
          setOverviewData(overviewData);
        } catch (err) {
          setError('Failed to load overview data');
          console.error('Error loading overview data:', err);
          setCategoryTasks([]);
        } finally {
          setLoading(false);
        }
      } else {
        setCategoryTasks([]);
      }
    };

    loadOverviewData();
  }, [selectedCategory, calculateOverviewData]);

  const loadFundSummaryData = async (projectId) => {
    setFundSummaryLoading(true);
    setFundSummary(createEmptyFundSummary());
    setFundSummaryError(null);
    try {
      const fundRes = await listFundRequests({ projectId, limit: 500, page: 1 });
      const rows = fundRes?.rows || fundRes?.data || fundRes?.fundRequests || [];
      const relevant = rows.filter((row) => ['approved', 'released'].includes(row.status));
      const releasedRows = relevant.filter((row) => row.status === 'released');
      const approvedRows = relevant.filter((row) => row.status === 'approved');

      const withTimestamps = relevant.map((row) => {
        const ts = row?.disbursement?.releasedAt || row?.plannedTransferDate || row?.updatedAt || row?.submissionDate || row?.createdAt;
        const parsed = ts ? new Date(ts).getTime() : NaN;
        return Number.isFinite(parsed) ? parsed : null;
      }).filter(Boolean);

      const totalAmount = relevant.reduce((sum, row) => {
        const amount = row?.disbursement?.amount ?? row?.totalAmount ?? 0;
        return sum + Number(amount || 0);
      }, 0);

      const lastReleaseTimestamp = withTimestamps.length ? Math.max(...withTimestamps) : null;
      const firstReleaseTimestamp = withTimestamps.length ? Math.min(...withTimestamps) : null;
      const totalRequests = relevant.length;
      const releasedCount = releasedRows.length;
      const approvedCount = approvedRows.length;
      const averagePerRequest = totalRequests > 0 ? totalAmount / totalRequests : 0;

      setFundSummary({
        totalAmount,
        totalRequests,
        releasedCount,
        approvedCount,
        averagePerRequest,
        lastReleaseDate: lastReleaseTimestamp ? new Date(lastReleaseTimestamp) : null,
        firstReleaseDate: firstReleaseTimestamp ? new Date(firstReleaseTimestamp) : null
      });
    } catch (err) {
      console.error('Error fetching fund disbursement summary:', err);
      setFundSummaryError('Tidak dapat memuat ringkasan dana cair');
    } finally {
      setFundSummaryLoading(false);
    }
  };

  // Handle unit bangunan selection
  const handleSelectUnit = async () => {
    if (!selectedProject || !selectedBangunan) {
      setError('Please select both project and unit bangunan');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [byUnit, byCategory, unitData] = await Promise.all([
        fetchDashboardByUnit(selectedProject),
        fetchDashboardByCategory(selectedProject),
        fetchUnitDashboardData(selectedProject, selectedBangunan)
      ]);
      
      const unitCategories = byCategory.filter(cat => cat.modularUnitId === parseInt(selectedBangunan));
      
      const unitProgressData = byUnit.find(unit => unit.moduleId === parseInt(selectedBangunan));
      const completePercent = unitProgressData ? computeTotalProgressPercent(unitProgressData) : 0;

      const progressPerKategori = unitCategories.map(cat => ({
        kategori: cat.workCategoryName,
        progress: computeTotalProgressPercent(cat),
        progressPeriodeLalu: parseFloat(cat.progressBeforeThisWeekPercentage) || 0,
        progressPeriodeIni: parseFloat(cat.progressThisWeekPercentage) || 0,
        progressTotal: computeTotalProgressPercent(cat)
      }));
      
      setUnitOverviewData({
        completePercent: completePercent || 0,
        progressPerKategori: sortCategoriesByRoman(progressPerKategori),
        tasksPerKategori: unitData.tasksPerKategori || [],
        milestoneDue: unitData.milestoneDue || [],
        lateTasks: unitData.lateTasks || []
      });
      
      setDataByUnit(byUnit);
      setDataByCategory(unitCategories);
      setIsUnitMode(true);
      setSelectedCategory('');
      
    } catch (err) {
      setError('Failed to load unit dashboard data');
      console.error('Error loading unit dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle project selection
  const handleSelectProject = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFundSummary(createEmptyFundSummary());
      setFundSummaryError(null);
      
      const projectData = await fetchProjectDashboardData(selectedProject);
      const [byProject, byUnit, byCategory] = await Promise.all([
        fetchDashboardByProject(selectedProject),
        fetchDashboardByUnit(selectedProject),
        fetchDashboardByCategory(selectedProject)
      ]);
      await loadFundSummaryData(selectedProject);
      
      setProjectOverviewData({
        completePercent: projectData.completePercent || 0,
        progressPerBangunan: projectData.progressPerBangunan || [],
        progressKategori: sortCategoriesByRoman(projectData.progressKategori || []),
        tasksPerKategori: projectData.tasksPerKategori || [],
        milestoneDue: projectData.milestoneDue || [],
        lateTasks: projectData.lateTasks || [],
        bangunanMilestones: projectData.bangunanMilestones || []
      });
      setDataByProject(byProject);
      setDataByUnit(byUnit);

      setDataByCategory(byCategory.map(cat => ({
        ...cat,
        quantity: parseFloat(cat.quantity) || 0,
        progressThisWeekVolume: parseFloat(cat.progressThisWeekVolume) || 0,
        progressBeforeThisWeekVolume: parseFloat(cat.progressBeforeThisWeekVolume) || 0,
        progress: computeTotalProgressPercent(cat),
        kategori: `${cat.modularUnitName} - ${cat.workCategoryName}`
      })));
      
      setIsProjectMode(true);
      setSelectedBangunan('');
      setSelectedCategory('');
      setIsUnitMode(false);
      
    } catch (err) {
      setError('Failed to load project dashboard data');
      console.error('Error loading project dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const { handleDownloadExcel, handleDownloadPDF, handleDownloadKategoriExcel, handleDownloadKategoriPDF } = useDashboardActions(setError);

  const currentPercent = isProjectMode
    ? (dataByProject && dataByProject.quantity
        ? computeTotalProgressPercent(dataByProject)
        : projectOverviewData.completePercent)
    : isUnitMode 
      ? unitOverviewData.completePercent 
      : overviewData.percentComplete;

  const normalizedPercent = Number.isFinite(currentPercent) ? Number(currentPercent) : 0;
  const percentColor = normalizedPercent >= 80 ? '#4CAF50' : normalizedPercent >= 50 ? '#FF9800' : '#F44336';

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Selector Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: 3,
          bgcolor: 'white',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            background: 'linear-gradient(135deg, #0f172a, #334155)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(15,23,42,0.2)'
          }}>
            <Typography sx={{ color: '#fff', fontSize: 16 }}>🔍</Typography>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              {t('PROJECT OVERVIEW')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              Pilih project, bangunan, atau kategori untuk melihat detail progress
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2} alignItems="flex-end" sx={{ position: 'relative' }}>
          <ProjectSelector
            projectList={projectList}
            selectedProject={selectedProject}
            loading={loading}
            isProjectMode={isProjectMode}
            onProjectChange={(value) => {
              setSelectedProject(value);
              setIsUnitMode(false);
              setIsProjectMode(false);
              setFundSummary(createEmptyFundSummary());
              setFundSummaryError(null);
              setFundSummaryLoading(false);
            }}
            onSelectProject={handleSelectProject}
          />
          <UnitSelector
            bangunanList={bangunanList}
            selectedProject={selectedProject}
            selectedBangunan={selectedBangunan}
            loading={loading}
            isProjectMode={isProjectMode}
            onUnitChange={(value) => {
              setSelectedBangunan(value);
              setIsUnitMode(false);
            }}
            onSelectUnit={handleSelectUnit}
          />
          <CategorySelector
            categoryList={categoryList}
            selectedBangunan={selectedBangunan}
            selectedCategory={selectedCategory}
            selectedProject={selectedProject}
            isUnitMode={isUnitMode}
            isProjectMode={isProjectMode}
            onCategoryChange={(value) => {
              setSelectedCategory(value);
              setIsProjectMode(false);
            }}
            onReset={() => {
              setSelectedCategory('');
              setIsUnitMode(false);
              setIsProjectMode(false);
              setOverviewData({
                percentComplete: 0,
                milestoneDue: [],
                lateTasks: [],
                categoryProgress: []
              });
              setCategoryTasks([]);
              setFundSummary(createEmptyFundSummary());
              setFundSummaryError(null);
              setFundSummaryLoading(false);
            }}
          />
        </Grid>
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      )}

      {(selectedCategory || isUnitMode || isProjectMode) ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 12 }}>
              <ProgressOverviewSection
                currentPercent={currentPercent}
                normalizedPercent={normalizedPercent}
                percentColor={percentColor}
                isProjectMode={isProjectMode}
                isUnitMode={isUnitMode}
              />

              {!selectedCategory && (
                <ChartSection
                  isProjectMode={isProjectMode}
                  isUnitMode={isUnitMode}
                  dataByUnit={dataByUnit}
                  dataByCategory={dataByCategory}
                  unitOverviewData={unitOverviewData}
                />
              )}

              {!selectedCategory && (
                <ProgressBangunanTable
                  isProjectMode={isProjectMode}
                  isUnitMode={isUnitMode}
                  dataByUnit={dataByUnit}
                  selectedBangunan={selectedBangunan}
                  onDownloadExcel={handleDownloadExcel}
                  onDownloadPDF={handleDownloadPDF}
                />
              )}

              <TaskPerKategoriTable
                isProjectMode={isProjectMode}
                isUnitMode={isUnitMode}
                projectList={projectList}
                bangunanList={bangunanList}
                categoryList={categoryList}
                selectedProject={selectedProject}
                selectedBangunan={selectedBangunan}
                selectedCategory={selectedCategory}
                dataByCategory={dataByCategory}
                unitOverviewData={unitOverviewData}
                categoryTasks={categoryTasks}
                onDownloadKategoriExcel={handleDownloadKategoriExcel}
                onDownloadKategoriPDF={handleDownloadKategoriPDF}
              />

            </Grid>

            <Grid size={{ xs: 12, lg: 12 }}>
              {/* Late Tasks - Redesigned */}
              <Card 
                sx={{ 
                  border: '1px solid #E2E8F0', 
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  px: 3, py: 2, 
                  background: 'linear-gradient(135deg, #FEF2F2 0%, #FFF5F5 100%)',
                  borderBottom: '1px solid #FECACA',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(239,68,68,0.3)' }}>
                      <Typography sx={{ fontSize: 16 }}>⚠️</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#991B1B', fontWeight: 700, lineHeight: 1.2 }}>
                        {t('TASK TERLAMBAT')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#EF4444' }}>Perlu perhatian segera</Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label={(isProjectMode ? projectOverviewData.lateTasks : isUnitMode ? unitOverviewData.lateTasks : overviewData.lateTasks).length + ' task'}
                    size="small"
                    sx={{ 
                      bgcolor: '#EF4444',
                      color: '#fff',
                      fontWeight: 700,
                      boxShadow: '0 2px 6px rgba(239,68,68,0.3)'
                    }}
                  />
                </Box>
                {(isProjectMode ? projectOverviewData.lateTasks : isUnitMode ? unitOverviewData.lateTasks : overviewData.lateTasks).length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('NAME')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('START')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('FINISH')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('DURATION')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('% COMPLETE')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('RESOURCE NAMES')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(isProjectMode ? projectOverviewData.lateTasks : isUnitMode ? unitOverviewData.lateTasks : overviewData.lateTasks).map((task, index) => (
                            <TableRow 
                              key={`${task.id}-3-${index}`}
                              sx={{ 
                                '&:hover': { bgcolor: '#F8FAFC' },
                                '&:last-child td': { border: 0 }
                              }}
                            >
                              <TableCell sx={{ py: 2 }}>{task.name || task.task}</TableCell>
                              <TableCell sx={{ py: 2 }}>{formatDate(task.start || task.startDate)}</TableCell>
                              <TableCell sx={{ py: 2 }}>{formatDate(task.finish || task.dueDate)}</TableCell>
                              <TableCell sx={{ py: 2 }}>{task.duration ? `${task.duration} ${t('HARI')}` : '-'}</TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Chip 
                                  label={`${task.percentComplete || task.progress || 0}%`}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    bgcolor: (task.percentComplete || task.progress || 0) >= 80 ? '#DCFCE7' : 
                                           (task.percentComplete || task.progress || 0) >= 50 ? '#FEF3C7' : '#FEE2E2',
                                    color: (task.percentComplete || task.progress || 0) >= 80 ? '#166534' : 
                                           (task.percentComplete || task.progress || 0) >= 50 ? '#92400E' : '#991B1B'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>{task.resourceNames || task.subcontractorName || 'Tim Proyek'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 5,
                      px: 3
                    }}>
                      <Typography variant="body1" sx={{ color: '#22C55E', fontWeight: 700, fontSize: '1.2rem', mb: 0.5 }}>✅</Typography>
                      <Typography variant="body2" color="#64748B" sx={{ fontWeight: 600 }}>
                        {t('TIDAK ADA TASK YANG TERLAMBAT')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>Semua task berjalan sesuai jadwal</Typography>
                    </Box>
                  )}
              </Card>

              {isProjectMode && (
                <FundSummarySection
                  fundSummary={fundSummary}
                  fundSummaryError={fundSummaryError}
                  fundSummaryLoading={fundSummaryLoading}
                  formatCurrency={formatCurrency}
                />
              )}
            </Grid>
          </Grid>
        )
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
          borderRadius: 3,
          border: '2px dashed #BFDBFE'
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '3rem', mb: 1 }}>📊</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              {t('Mulai dengan memilih Project')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', maxWidth: 400, mx: 'auto' }}>
              Ikuti 3 langkah di atas untuk melihat detail progress pembangunan
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {[
              { step: '1', label: 'Project', done: !!selectedProject },
              { step: '2', label: 'Unit Bangunan', done: !!selectedBangunan },
              { step: '3', label: 'Klik Pilih / Pilih Kategori', done: isUnitMode || isProjectMode || !!selectedCategory },
            ].map((item) => (
              <Box key={item.step} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: item.done ? '#DCFCE7' : '#F1F5F9', border: `1px solid ${item.done ? '#86EFAC' : '#E2E8F0'}` }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: item.done ? '#22C55E' : '#CBD5E1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                  {item.done ? '✓' : item.step}
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: item.done ? '#166534' : '#64748B' }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProjectOverviewComponent;
